// Adapts a versioned *interventiebibliotheek* document into the flat, per-unit
// shape the materials calculation consumes.
//
// This is NOT adaptMeetstandaardDocument's job. That one turns `effecten` into
// questions for the effect picker; this document has no statements to answer —
// it carries kengetallen (physical effect per unit). Two document kinds, two
// adapters, no kind-switch inside one function.
//
// THREE FAMILIES, and they are not interchangeable:
//
//   perYear  — recurring: kengetallen.gasM3PerJaar etc. produce
//              berekend.co2eKgPerJaar + berekend.besparingHuishoudenEurPerJaar (57 of 114)
//   perUnit  — one-off: kengetallen.co2ePerEenheid (15 of 114)
//   neither  — enabler / casusgebonden / needs verification (42 of 114)
//
// The third family must surface as *unknown*, never as 0. The document says so
// itself: controle.zonderKengetal entries "mogen niet als nul worden gelezen".
// A zero would render as a measured finding of "this has no effect".
//
// 0.9 WAS REPUBLISHED IN PLACE on 2026-08-17 — same version id, 95 interventies
// grown to 114, and `berekend` renamed: besparingEurPerJaar became
// besparingHuishoudenEurPerJaar, monetairCo2EurPerEenheid became
// maatschappelijkeBesparingEurPerEenheid, with maatschappelijkeBesparingEurPerJaar
// and totaleWaardeEurPerJaar added. So the version pin no longer guarantees a
// fixed body, and reading only one spelling of the household saving is how this
// silently became EUR 0,00 on the Besparingen card. Both names are read, and a
// per-year interventie carrying neither is refused rather than nulled.
//
// totaleWaardeEurPerJaar is deliberately NOT surfaced: it adds a household's own
// money to a societal value, which the standaard itself flags as "geen kasgeld",
// and the Besparingen card reports the two components separately on purpose.
//
// AI-CONTEXT: See docs/features/materials-co2-circulariteit.md

const CO2_PRICE_UNIT = 'EUR/kg CO2e'

/**
 * @typedef {object} AdaptedInterventie
 * @property {string} code - Document id, e.g. 'ET01' or 'textiel-hergebruiken'
 * @property {string} slug
 * @property {string} name - Human label, e.g. 'Radiatorfolie plaatsen'
 * @property {string} domein - 'Klimaat & Energie' | 'Circulariteit' | 'Biodiversiteit & Natuur'
 * @property {string} activiteitstype - Wat voor soort interventie het is, bv. 'Energie besparen'
 * @property {string} unit - Registration unit as the standaard words it, e.g. 'm2 folie'
 * @property {'perYear'|'perUnit'|null} basis - Time basis; null when there is no kengetal
 * @property {'ok'|'no-kengetal'} status
 * @property {'perUnit'|'perResident'|'perDwelling'} scope - How a registered quantity scales
 * @property {number|null} co2KgPerUnit - kg CO2e per registration unit (per year when basis is perYear)
 * @property {number|null} privateEurPerUnit - Household bill saving €/yr; null for one-off interventies
 * @property {number|null} societalEurPerUnit - CO2 valued at the document's shadow price
 * @property {string} bewijssterkte - 'Hoog' | 'Middel' | 'Laag' | …
 * @property {string} statusKengetal
 * @property {string} onderbouwing
 * @property {string} monetarisatiebron
 * @property {string} afbakening - Overlap rule that applies to this interventie
 *
 * @typedef {object} AdaptedInterventieLibrary
 * @property {string} standaard
 * @property {string} version
 * @property {string} label
 * @property {number} co2PriceEurPerKg
 * @property {Array<AdaptedInterventie>} interventies
 * @property {Object<string, AdaptedInterventie>} byCode
 */

/**
 * How a registered quantity turns into an effect.
 *
 * The standaard encodes this only in the eenheid string, so the rule lives
 * here rather than being re-derived by every consumer. 'per bewoner' needs the
 * household's resident count; 'per woning' / 'per huishouden' applies once
 * however many items were handed out.
 *
 * @param {string} eenheid
 * @returns {'perUnit'|'perResident'|'perDwelling'}
 */
const scopeOf = (eenheid) => {
    const value = (eenheid || '').toLowerCase()
    if (value.includes('bewoner') || value.includes('persoon')) return 'perResident'
    if (value.includes('woning') || value.includes('huishouden')) return 'perDwelling'
    return 'perUnit'
}

/**
 * The single shadow price the whole document is monetised with.
 *
 * Refuses to fall back to a remembered constant: the standaard says to pick one
 * price and report it, so a document that stops publishing one is a change we
 * must notice, not paper over.
 *
 * @param {Array<{eenheid: string, waarde: number}>} aannames
 * @returns {number}
 */
const co2PriceFrom = (aannames) => {
    const entry = (aannames || []).find((a) => a?.eenheid === CO2_PRICE_UNIT)
    if (!entry || typeof entry.waarde !== 'number') {
        throw new Error(
            `adaptInterventieDocument: no CO2 schaduwprijs ('${CO2_PRICE_UNIT}') in aannames`
        )
    }
    return entry.waarde
}

/**
 * The household's yearly bill saving, under either spelling 0.9 has shipped.
 *
 * Not a tolerant lookup: a per-year interventie that publishes neither name is a
 * schema change we must notice. Returning null there would flow into
 * computeHouseholdImpact's sum as a 0 and render as a measured "saved nothing".
 *
 * @param {object} berekend
 * @param {string} code - For the error message
 * @returns {number}
 */
const householdSavingFrom = (berekend, code) => {
    // besparingEurPerJaar is the pre-2026-08-17 name. A browser or the Google
    // Frontend can still hand us that body for a day after the republish
    // (cache-control: max-age=86400), and it is older, not wrong.
    const value = typeof berekend.besparingHuishoudenEurPerJaar === 'number'
        ? berekend.besparingHuishoudenEurPerJaar
        : berekend.besparingEurPerJaar

    if (typeof value !== 'number') {
        throw new Error(
            `adaptInterventieDocument: '${code}' has a yearly CO2 figure but no `
            + 'besparingHuishoudenEurPerJaar (nor the legacy besparingEurPerJaar)'
        )
    }
    return value
}

/**
 * @param {object} raw - One entry from `interventies`
 * @param {number} co2Price
 * @returns {AdaptedInterventie}
 */
const adaptOne = (raw, co2Price) => {
    const berekend = raw.berekend || {}
    const kengetallen = raw.kengetallen || {}

    const perYearCo2 = berekend.co2eKgPerJaar
    const perUnitCo2 = kengetallen.co2ePerEenheid

    // Order matters: a per-year interventie also has a co2ePerEenheid slot, but
    // it is null. Checking the yearly figure first keeps the families disjoint.
    let basis = null
    let co2KgPerUnit = null
    let privateEurPerUnit = null

    if (typeof perYearCo2 === 'number') {
        basis = 'perYear'
        co2KgPerUnit = perYearCo2
        privateEurPerUnit = householdSavingFrom(berekend, raw.id)
    } else if (typeof perUnitCo2 === 'number') {
        basis = 'perUnit'
        co2KgPerUnit = perUnitCo2
        // A reuse or repair avoids a purchase; it lowers nobody's energy bill.
        privateEurPerUnit = null
    }

    return {
        code: raw.id,
        slug: raw.slug || raw.id,
        name: raw.interventie || '',
        domein: raw.domein || '',
        // Gebruikt om de coachadviezen te groeperen; de standaard hanteert deze
        // indeling zelf, dus een eigen thema-indeling zou er alleen naast gaan
        // liggen.
        activiteitstype: raw.activiteitstype || '',
        unit: raw.eenheid || '',
        basis,
        status: basis === null ? 'no-kengetal' : 'ok',
        scope: scopeOf(raw.eenheid),
        co2KgPerUnit,
        privateEurPerUnit,
        // Derived from the one price rather than read from
        // berekend.maatschappelijkeBesparingEurPerEenheid / …EurPerJaar, so that
        // every family is monetised at the same rate. A test asserts this
        // reproduces the published figures wherever the document publishes them.
        societalEurPerUnit: co2KgPerUnit === null ? null : co2KgPerUnit * co2Price,
        bewijssterkte: raw.bewijssterkte || '',
        statusKengetal: raw.statusKengetal || '',
        onderbouwing: raw.onderbouwing || '',
        monetarisatiebron: raw.monetarisatiebron || '',
        afbakening: raw.afbakening || '',
    }
}

/**
 * @param {object} document - A published interventiebibliotheek document
 * @returns {AdaptedInterventieLibrary}
 */
const adaptInterventieDocument = (document) => {
    const meta = document?.meta || {}

    if (meta.kind !== 'interventiebibliotheek') {
        // The effect standaarden share this URL shape but carry `effecten`.
        // Adapting one here would silently yield an empty, legitimate-looking
        // library instead of an error.
        throw new Error(
            `adaptInterventieDocument: expected an interventiebibliotheek, got kind '${meta.kind}'`
        )
    }

    const co2PriceEurPerKg = co2PriceFrom(document.aannames)
    const interventies = (document.interventies || []).map((raw) => adaptOne(raw, co2PriceEurPerKg))

    const byCode = {}
    interventies.forEach((item) => { byCode[item.code] = item })

    return {
        standaard: meta.standaard || '',
        version: meta.version || '',
        label: meta.label || '',
        co2PriceEurPerKg,
        interventies,
        byCode,
    }
}

export default adaptInterventieDocument
